import { useMemo, type JSX, type CSSProperties, type ComponentType } from 'react';
import {
  Ghost as LucideGhost,
  Heart as LucideHeart,
  Star as LucideStar,
  TriangleAlert as LucideTriangleAlert,
  Eye as LucideEye,
  Skull as LucideSkull
} from 'lucide-react';
import type { LucideProps } from 'lucide-react';
import type { StatusDisplayConfig, StatusDisplayItem, StatusColorThreshold } from 'shared';

// P8-2a（P7-5 の適用漏れ）: 絵文字アイコンを lucide へ統一。
// lucide の icons レコード直渡しは現行 @types/react で TS2786 になるため、
// foundation/primitives/Icon.tsx と同じ ComponentType<LucideProps> 正規化で受ける。
type IconComponent = ComponentType<LucideProps>;
const STATUS_ICONS: Record<string, IconComponent> = {
  ghost: LucideGhost as unknown as IconComponent,
  heart: LucideHeart as unknown as IconComponent,
  star: LucideStar as unknown as IconComponent,
  warning: LucideTriangleAlert as unknown as IconComponent,
  eye: LucideEye as unknown as IconComponent,
  skull: LucideSkull as unknown as IconComponent
};

export interface StatusHUDProps {
  config: StatusDisplayConfig;
  variables: Record<string, unknown>;
}

/**
 * ステータスHUDコンポーネント
 * シナリオのui.statusDisplay設定に基づいて変数を画面に表示
 */
export function StatusHUD({ config, variables }: StatusHUDProps): JSX.Element | null {
  if (!config.enabled || !config.items || config.items.length === 0) {
    return null;
  }

  const position = config.position ?? 'top-right';
  const positionStyle = useMemo((): CSSProperties => {
    switch (position) {
      case 'top-left':
        return { top: 16, left: 16 };
      case 'top-right':
        return { top: 16, right: 16 };
      case 'bottom-left':
        return { bottom: 80, left: 16 }; // 80px to avoid clue button
      case 'bottom-right':
        return { bottom: 80, right: 16 }; // 80px to avoid bottom-right resident UI (e.g. LINE history button; the advance button was removed in P7)
      default:
        return { top: 16, right: 16 };
    }
  }, [position]);

  const containerStyle: CSSProperties = {
    position: 'absolute',
    ...positionStyle,
    // P8-2a: z-index はインライン生値 90 をやめ、.status-hud（09-hud-windows.css）の
    // var(--z-status-hud)（同値 90）に一元化した。
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    pointerEvents: 'none',
  };

  return (
    <div className="status-hud" style={containerStyle}>
      {config.items.map((item) => (
        <StatusItem
          key={item.variableKey}
          item={item}
          value={variables[item.variableKey]}
          style={config.style}
        />
      ))}
    </div>
  );
}

interface StatusItemProps {
  item: StatusDisplayItem;
  value: unknown;
  style?: StatusDisplayConfig['style'];
}

function StatusItem({ item, value, style }: StatusItemProps): JSX.Element | null {
  // showWhen条件のチェック
  if (item.showWhen === 'nonzero' && (value === 0 || value === false || value === null || value === undefined)) {
    return null;
  }

  const numericValue = typeof value === 'number' ? value : 0;
  const displayValue = formatValue(item.format, value);
  const color = getColorForValue(numericValue, item.colorThresholds);
  const isWarning = item.warningThreshold !== undefined && numericValue <= item.warningThreshold;

  const itemStyle: CSSProperties = {
    backgroundColor: style?.backgroundColor ?? 'rgba(0, 0, 0, 0.7)',
    borderColor: style?.borderColor ?? 'rgba(255, 255, 255, 0.2)',
    color: color ?? style?.textColor ?? '#e0e0e0',
    fontFamily: style?.fontFamily ?? 'monospace',
    fontSize: style?.fontSize ?? '0.875rem',
    padding: '8px 12px',
    borderRadius: 8,
    border: `1px solid ${style?.borderColor ?? 'rgba(255, 255, 255, 0.2)'}`,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minWidth: 80,
  };

  const iconElement = item.icon ? getIconElement(item.icon) : null;
  const animationClass = isWarning && item.warningAnimation === 'pulse' ? 'status-hud__pulse' : '';

  return (
    <div className={`status-hud__item ${animationClass}`} style={itemStyle}>
      {iconElement && <span className="status-hud__icon">{iconElement}</span>}
      <span className="status-hud__label">{item.label}</span>
      <span className="status-hud__value" style={{ fontWeight: 'bold', color }}>
        {displayValue}
      </span>
    </div>
  );
}

function formatValue(format: string | undefined, value: unknown): string {
  if (value === null || value === undefined) {
    return '---';
  }
  if (!format) {
    return String(value);
  }
  return format.replace('{{value}}', String(value));
}

function getColorForValue(value: number, thresholds?: StatusColorThreshold[]): string | undefined {
  if (!thresholds || thresholds.length === 0) {
    return undefined;
  }

  // 閾値を降順でソート（大きい値から小さい値へ）
  const sorted = [...thresholds].sort((a, b) => b.value - a.value);

  // 値がどの閾値に該当するか判定
  for (const threshold of sorted) {
    if (value >= threshold.value) {
      return threshold.color;
    }
  }

  // どの閾値にも該当しない場合は最後（最小）の色を使用
  return sorted[sorted.length - 1]?.color;
}

function getIconElement(iconName: string): JSX.Element | null {
  const IconComp = STATUS_ICONS[iconName];
  if (!IconComp) {
    return null;
  }
  // span（.status-hud__icon）側が font-size: 1.2em を持つため、svg は 1em で
  // 従来の絵文字と同じ相対スケールになる。色は stroke=currentColor＝閾値色/テキスト色に追従。
  return <IconComp size="1em" aria-hidden />;
}

export default StatusHUD;
